import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Circle,
  Gem,
  Disc,
  Hammer,
  Cpu,
  Flag,
  Gift,
  DiscAlbum,
  Settings2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import "./ProductSegment.css";
import Footer from "../components/Footer";
import Header from "../components/Header";
import segmentData from "../utils/segmentData";
import { useBuyer } from "../contexts/BuyerContext";
import {
  getOrderedPurityKeys,
  getOrderedSegments,
} from "../utils/productHelpers";
import { orderedKeysOf } from "../utils/orderedJson";
import {
  pushNavigationState,
  getSegmentState,
  clearSegmentCache,
  getLastSelectedPurity,
  getSegmentPurity,
  setSegmentPurity,
} from "../utils/segmentCache";

const DiamondIcon = Gem;
const PrecisionManufacturingIcon = Settings2;

const getSegmentIcon = (segment) => {
  const iconMap = {
    GOLD: Circle,
    SILVER: Disc,
    DIAMOND: Gem,
    PLATINUM: DiscAlbum,
    GEMS: DiamondIcon,
    PEARL: Circle,
    BANDHEL: Circle,
    DECOR: Gift,
    BRAND: Flag,
    TOOL: Hammer,
    MACHINE: PrecisionManufacturingIcon,
    TECH: Cpu,
  };
  return iconMap[segment] || Circle;
};

const getSegmentDisplayName = (segment) => {
  const nameMap = {
    GOLD: "GOLD",
    SILVER: "SILVER",
    DIAMOND: "DIAMOND",
    PLATINUM: "PLATINUM",
    GEMS: "GEMS",
    PEARLS: "PEARLS",
    BANDHEL: "BANDHEL",
    DECOR: "DECOR",
    BRAND: "BRAND",
    TOOLS: "TOOLS",
    MACHINE: "MACHINE",
    TECH: "TECH",
  };
  return nameMap[segment] || segment;
};

const PuritySelector = ({ purityOptions, selectedPurity, onSelect }) => (
  <div className="purity-container">
    {purityOptions.map((option, index) => (
      <button
        key={index}
        className={`purity-button ${selectedPurity === option ? "active" : ""}`}
        onClick={() => onSelect(option)}
      >
        {option}
      </button>
    ))}
  </div>
);

const SegmentBar = ({ segments, selectedSegment, onSelect }) => (
  <div className="segmentbar">
    {segments.map((segment, index) => {
      const IconComponent = getSegmentIcon(segment);
      const displayName = getSegmentDisplayName(segment);
      return (
        <div
          key={index}
          className={`metal-type ${
            selectedSegment === segment ? "active" : ""
          }`}
          onClick={() => onSelect(segment)}
        >
          <div
            className="icon-container"
            style={{
              backgroundColor:
                selectedSegment === segment ? "#DAA520" : "transparent",
            }}
          >
            <IconComponent
              size={20}
              strokeWidth={1.5}
              style={{
                color: selectedSegment === segment ? "#FFFFFF" : "#777",
              }}
            />
          </div>
          <span className="metal-label">{displayName}</span>
        </div>
      );
    })}
  </div>
);

const CategoriesGrid = ({
  categories,
  onCategoryClick,
  currentSegment,
  currentPurity,
}) => {
  const navigate = useNavigate();

  const handleClick = (category) => {
    const encodedSegment = encodeURIComponent(
      currentSegment.toLowerCase().replace(/\s+/g, "-"),
    );
    const encodedPurity = encodeURIComponent(
      currentPurity.toLowerCase().replace(/\s+/g, ""),
    );
    const encodedCategory = encodeURIComponent(
      category.toLowerCase().replace(/\s+/g, "-"),
    );
    const path = `/productSegment/${encodedSegment}/${encodedPurity}/${encodedCategory}`;

    if (onCategoryClick) {
      onCategoryClick(category, path);
    } else {
      navigate(path);
    }
  };

  const categoryObjects = categories.map((category) => ({
    title: category,
    image:
      segmentData[currentSegment]?.[currentPurity]?.[category]?.image ||
      "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=500&fit=crop",
  }));

  return (
    <div className="categories-grid">
      {categoryObjects.map((category, index) => (
        <div
          key={index}
          className="category-card"
          onClick={() => handleClick(category.title)}
        >
          <div
            className="category-image"
            style={{ backgroundImage: `url(${category.image})` }}
          />
          <div className="category-content">
            <h3 className="category-title">{category.title}</h3>
            <span className="arrow">›</span>
          </div>
        </div>
      ))}
    </div>
  );
};

function getValidPurityForSegment(seg) {
  const purityOptions = segmentData[seg] ? getOrderedPurityKeys(seg) : [];
  const segmentPurity = getSegmentPurity(seg);
  if (segmentPurity && purityOptions.includes(segmentPurity))
    return segmentPurity;
  const lastPurity = getLastSelectedPurity();
  if (lastPurity && purityOptions.includes(lastPurity)) return lastPurity;
  return purityOptions[0] || "";
}

const ProductSegment = () => {
  const { segment, purity } = useParams();

  const { refreshSegmentData } = useBuyer();
  const navigate = useNavigate();
  const scrollableRef = useRef(null);
  const lastUrlSegmentRef = useRef();
  const normalizedSegment = (segment || "").toUpperCase();
  const normalizedPurity = purity ? decodeURIComponent(purity) : null;

  const initialSegment = segmentData[normalizedSegment]
    ? normalizedSegment
    : Object.keys(segmentData).find(
        (k) => typeof segmentData[k] === "object" && segmentData[k] !== null,
      );

  const [selectedSegment, setSelectedSegment] = useState(() => {
    if (normalizedSegment && segmentData[normalizedSegment]) {
      return normalizedSegment;
    }
    const savedSegment = localStorage.getItem("selectedSegment");
    if (savedSegment && segmentData[savedSegment]) {
      return savedSegment;
    }
    const memoryState = getSegmentState();
    const cachedSegment = memoryState?.selectedSegment;
    if (cachedSegment && segmentData[cachedSegment]) {
      return cachedSegment;
    }
    return initialSegment;
  });

  const [selectedPurity, setSelectedPurity] = useState(() => {
    const purityOptions = segmentData[selectedSegment]
      ? getOrderedPurityKeys(selectedSegment)
      : [];

    // ✅ If purity is in URL params, use it if valid
    if (normalizedPurity && purityOptions.includes(normalizedPurity)) {
      return normalizedPurity;
    }

    const lastPurity = getSegmentPurity(selectedSegment);
    if (lastPurity && purityOptions.includes(lastPurity)) return lastPurity;
    return purityOptions[0] || "";
  });

  useEffect(() => {
    refreshSegmentData();
  }, [refreshSegmentData]);

  const handleSegmentSelect = useCallback(
    (seg) => {
      if (!segmentData[seg]) return;
      const validPurity = getValidPurityForSegment(seg);
      setSelectedSegment(seg);
      setSelectedPurity(validPurity);
      setSegmentPurity(seg, validPurity);
      navigate(`/productSegment/${encodeURIComponent(seg)}`, { replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    if (!normalizedSegment || !segmentData[normalizedSegment]) return;
    if (lastUrlSegmentRef.current === normalizedSegment) return;
    lastUrlSegmentRef.current = normalizedSegment;
    const validPurity = getValidPurityForSegment(normalizedSegment);
    setSelectedSegment(normalizedSegment);
    setSelectedPurity(validPurity);
  }, [normalizedSegment]);

  // ✅ NEW: Handle purity changes from URL params
  useEffect(() => {
    if (!normalizedPurity || !segmentData[selectedSegment]) return;
    const purityOptions = getOrderedPurityKeys(selectedSegment);
    if (purityOptions.includes(normalizedPurity)) {
      setSelectedPurity(normalizedPurity);
      setSegmentPurity(selectedSegment, normalizedPurity);
    }
  }, [normalizedPurity, selectedSegment]);

  const segments = getOrderedSegments();
  const purityOptions = segmentData[selectedSegment]
    ? getOrderedPurityKeys(selectedSegment)
    : [];

  const productCategories = segmentData[selectedSegment]?.[selectedPurity]
    ? orderedKeysOf(segmentData[selectedSegment][selectedPurity])
    : [];

  const handleCategoryClick = (category, path) => {
    pushNavigationState(selectedSegment, selectedPurity, category);
    navigate(path);
  };

  const handlePuritySelect = useCallback(
    (newPurity) => {
      setSelectedPurity(newPurity);
      setSegmentPurity(selectedSegment, newPurity);

      // ✅ Navigate to purity route: /productSegment/:segment/:purity
      const encodedSegment = encodeURIComponent(selectedSegment.toLowerCase());
      const encodedPurity = encodeURIComponent(newPurity.toLowerCase());
      navigate(`/productSegment/${encodedSegment}/${encodedPurity}`, {
        replace: false,
      });
    },
    [selectedSegment, navigate],
  );

  useEffect(() => {
    if (segmentData[selectedSegment]) {
      clearSegmentCache();
    }
  }, [selectedSegment]);

  useEffect(() => {
    const scrollTimer = setTimeout(() => {
      const el =
        scrollableRef.current || document.querySelector(".scrollable-area");
      const html = document.documentElement;

      html.style.scrollBehavior = "auto";

      if (el) {
        el.scrollTop = 0;
      }
      window.scrollTo(0, 0);

      html.style.scrollBehavior = "";
    }, 100);

    return () => clearTimeout(scrollTimer);
  }, [selectedSegment, selectedPurity]);

  useEffect(() => {
    localStorage.setItem("selectedSegment", selectedSegment);
    localStorage.setItem("selectedPurity", selectedPurity);
  }, [selectedSegment, selectedPurity]);

  return (
    <>
      <div className="header-wrapper">
        <Header title="Product Segment" />
      </div>
      <div className="maincontent">
        <SegmentBar
          segments={segments}
          selectedSegment={selectedSegment}
          onSelect={handleSegmentSelect}
        />

        <div className="content-wrapper">
          <div className="purity-wrapper">
            <PuritySelector
              purityOptions={purityOptions}
              selectedPurity={selectedPurity}
              onSelect={handlePuritySelect}
            />
          </div>
          <div
            className="scrollable-area"
            key={selectedSegment}
            ref={scrollableRef}
          >
            <CategoriesGrid
              categories={productCategories}
              onCategoryClick={handleCategoryClick}
              currentSegment={selectedSegment}
              currentPurity={selectedPurity}
            />
          </div>
        </div>
      </div>
      <Footer catalogueRoute={`/productSegment/${selectedSegment}`} />
    </>
  );
};

export default ProductSegment;
